import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserLabel } from '@v2/components/labels/UserLabel/UserLabel';
import { getObjectKeys } from '@/lib/helpers/objects/getObjectKeys';
import { IResource, IUserFull } from '@gettactic/api';
import { capitalize } from '@gettactic/helpers';
import { ResourceLabel } from '@v2/components/labels/ResourceLabel/ResourceLabel';
import { VirtualizedList } from '@v2/components/display/VirtualizedList/VirtualizedList';
import { MapFiltersCardSearchNoResults } from '@webV2/components/cards/MapFiltersCard/MapFiltersCardSearchNoResults';
import { MapFiltersCardClasses as classes } from '@webV2/components/cards/MapFiltersCard/MapFiltersCard';
import { ResourceWithSchedules } from '@webV2/components/cards/MapFiltersCard/helpers';
import { useGlobalSearch } from "@webV2/components/global-search/GlobalSearchProvider";

export interface MapSearchResultsProps {
  users: IUserFull[];
  resources: ResourceWithSchedules[];
  setResourceIdHovering: (resource: IResource | null) => void;
  onBook: (resourceId: IResource | null) => void;
  showSchedules?: boolean;
  showAllSchedulesAsResources?: boolean;
  showScheduleOfUserId?: string;
}

export const MapSGroupedResources: React.FC<MapSearchResultsProps> = ({
  users,
  resources: definedResources,
  setResourceIdHovering,
  onBook,
  showSchedules = false,
  showScheduleOfUserId,
  showAllSchedulesAsResources = false
}) => {
  const children: React.ReactNode[] = [];
  const { searchQuery } = useGlobalSearch();
  const { t } = useTranslation('common');

  const resources = React.useMemo(() => {
    const groupedResources: Record<string, ResourceWithSchedules[]> = {};
    definedResources.forEach((resource) => {
      const group = String(resource.type || 'Other').replace('_', ' ');
      if (!groupedResources[group]) {
        groupedResources[group] = [];
      }

      if (showAllSchedulesAsResources) {
        resource.schedules?.forEach((schedule) => {
          const scheduleResource: ResourceWithSchedules = {
            ...resource,
            schedules: [schedule]
          };
          groupedResources[group].push(scheduleResource);
        });
        return;
      }

      groupedResources[group].push(resource);
    });

    // Sort all resources in groups by start time of their first schedule

    Object.keys(groupedResources).forEach((key) => {
      groupedResources[key].sort((a, b) => {
        const aStart = a.schedules?.[0]?.start || 0;
        const bStart = b.schedules?.[0]?.start || 0;
        return new Date(aStart).getTime() - new Date(bStart).getTime();
      });
    });

    // Sort groupedResources by group name
    const sortedKeys = Object.keys(groupedResources).sort((a, b) =>
      a.localeCompare(b)
    );
    const sortedGroupedResources: Record<string, ResourceWithSchedules[]> = {};
    sortedKeys.forEach((key) => {
      sortedGroupedResources[key] = groupedResources[key];
    });

    return sortedGroupedResources;
  }, [definedResources, showAllSchedulesAsResources]);

  if (users.length > 0) {
    children.push(
      <div key={'people'} className={'flex h-full items-center text-xl'}>
        {t('terms.people')} ({users.length})
      </div>
    );

    users.forEach((user) => {
      children.push(
        <UserLabel
          className={classes.ListItem}
          user={user}
          key={user.id}
          allowFavorite
        />
      );
    });
  }

  getObjectKeys(resources).forEach((key) => {
    const groupKey = String(key);
    const groupResources = (resources[key] || []) as IResource[];
    const total = groupResources.length;
    if (!total) return;

    children.push(
      <div key={groupKey} className={'flex h-full items-center text-xl'}>
        {capitalize(groupKey)} ({total})
      </div>
    );

    (resources[key] || []).forEach((resource) => {
      const schedules = showSchedules
        ? showScheduleOfUserId
          ? resource.schedules?.filter((schedule) => {
              return schedule.slots?.some(
                (slot) => slot.user?.id === showScheduleOfUserId
              );
            })
          : resource.schedules
        : undefined;

      if ((schedules?.length || 0) > 0)
        children.push(
          <ResourceLabel
            className={classes.ListItem}
            resource={resource}
            key={resource.id}
            onMouseEnter={() => {
              setResourceIdHovering(resource);
            }}
            onClick={() => onBook(resource)}
            onMouseLeave={() => setResourceIdHovering(null)}
            schedules={schedules}
            showHierarchy={!showSchedules}
          />
        );
    });
  });

  if (!children?.length) {
    return <MapFiltersCardSearchNoResults searchText={searchQuery} />;
  }

  return (
    <VirtualizedList
      itemHeight={86}
      scrollAreaProps={{
        className: classes.TabContentScrollArea
      }}
    >
      {children}
    </VirtualizedList>
  );
};
